import { ComponentProps, FC, MouseEvent, ReactNode, useCallback } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { Link } from '@robocorp/components';
import { styled } from '@robocorp/theme';

import { usePreload } from '../hooks';

interface OwnProps extends Omit<ComponentProps<typeof Link>, 'href' | 'onClick'> {
  to: string;
  link?: boolean;
  extLink?: boolean;
  onClick?: (e: MouseEvent) => void;
  exactMatch?: boolean;
  preventDefault?: boolean;
}

type Props = OwnProps & {
  className?: string;
  children?: ReactNode;
  variant?: 'primary' | 'subtle' | 'ghost';
};

const StyledAnchor = styled.a`
  color: ${({ theme }) => theme.color('purple70')};
  &:hover {
    color: ${({ theme }) => theme.color('purple50')};
  }
`;

const NavLink: FC<Props> = ({
  children,
  link,
  extLink,
  to,
  variant,
  onClick,
  exactMatch,
  preventDefault,
  ...restProps
}) => {
  const preloadRoute = usePreload();
  const match = useMatch({
    path: to,
    end: exactMatch || false,
  });

  const navigate = useNavigate();

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();

    if (e.metaKey || e.shiftKey) {
      return;
    }

    if (onClick) {
      onClick(e);
    }

    if (preventDefault) {
      e.preventDefault();
      return;
    }

    if (!link && !extLink) {
      e.preventDefault();
      navigate(to);
    }
  };

  const onMouseEnter = useCallback(() => {
    preloadRoute(to);
  }, [to]);

  if (variant) {
    return (
      <Link
        href={to}
        onClick={handleClick}
        aria-current={match ? 'page' : undefined}
        variant={variant}
        target={extLink ? '_blank' : '_self' /* HTML default */}
        onMouseOver={onMouseEnter}
        {...restProps}
      >
        {children}
      </Link>
    );
  }

  return (
    <StyledAnchor
      href={to}
      onClick={handleClick}
      target={extLink ? '_blank' : undefined}
      rel="noreferrer"
      {...restProps}
    >
      {children}
    </StyledAnchor>
  );
};

export default NavLink as FC<Props>;
